import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  fetchBuyerInfrastructureAgreementPage,
  fetchInfrastructureAgreementPage,
  patchInfrastructureAgreementDocuments,
  postBuyerUnwindPendingSignatureDeal,
} from '../../services/infrastructureLeasingListing.service';
import InfrastructureLeasingAgreementSignPanel from '../../components/infrastructureLeasing/InfrastructureLeasingAgreementSignPanel';
import InfrastructureLeasingBuyerPostSaleTabs from '../../components/infrastructureLeasing/InfrastructureLeasingBuyerPostSaleTabs';
import InfrastructureLeasingDisputePanel from '../../components/infrastructureLeasing/InfrastructureLeasingDisputePanel';
import CommonService from '../../services/common.service';
import { resolvePublicFileUrl } from '../../constants/api-urls';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../../assets/styles/infrastructure_leasing_seller_listing_enquiries.scss';
import '../../assets/styles/infrastructure_leasing_buyer_post_sale.scss';

function formatDateIso(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

function formatDateOnly(s) {
  if (!s) return '—';
  try {
    return new Date(`${String(s).slice(0, 10)}T12:00:00`).toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return s;
  }
}

function parseAgreementAttachments(agr) {
  if (!agr?.attachments) return [];
  const a = agr.attachments;
  if (Array.isArray(a)) return a.filter((x) => x && (x.path || x.accessPath));
  if (typeof a === 'string') {
    try {
      const p = JSON.parse(a);
      return Array.isArray(p) ? p.filter((x) => x && (x.path || x.accessPath)) : [];
    } catch {
      return [];
    }
  }
  return [];
}

function attachmentHref(row) {
  const p = row?.path || row?.accessPath;
  return p ? resolvePublicFileUrl(p) : '';
}

function statusBadgeClass(statusKey) {
  const s = String(statusKey || '').toLowerCase();
  if (s === 'active') return 'bg-success';
  if (s === 'completed') return 'bg-info text-dark';
  if (s === 'pending_signature') return 'bg-warning text-dark';
  if (s === 'draft') return 'bg-secondary';
  if (s === 'cancelled') return 'bg-danger';
  return 'bg-secondary';
}

function SectionCard({ title, children, className = '', stretch = false }) {
  const marginClass = stretch ? '' : 'mb-3';
  return (
    <div
      className={`card border shadow-sm ${marginClass} ${stretch ? 'h-100' : ''} ${className}`.trim()}
    >
      <div className="card-header bg-white fw-semibold py-2">{title}</div>
      <div className="card-body small py-3">{children}</div>
    </div>
  );
}

function DlRow({ label, value }) {
  return (
    <>
      <dt className="col-sm-4 text-muted">{label}</dt>
      <dd className="col-sm-8 mb-2">{value ?? '—'}</dd>
    </>
  );
}

export default function InfrastructureLeasingAgreementDetailsPage() {
  const { listingId: listingParam, enquiryId: enquiryParam } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isBuyerView = useMemo(
    () => /\/infrastructure-leasing\/my-enquiries\/[^/]+\/[^/]+\/agreement/.test(location.pathname),
    [location.pathname]
  );

  const listingIdNum = useMemo(() => {
    const n = parseInt(String(listingParam), 10);
    return Number.isFinite(n) && n >= 1 ? n : NaN;
  }, [listingParam]);
  const enquiryIdNum = useMemo(() => {
    const n = parseInt(String(enquiryParam), 10);
    return Number.isFinite(n) && n >= 1 ? n : NaN;
  }, [enquiryParam]);

  const [loading, setLoading] = useState(true);
  const [errorKey, setErrorKey] = useState(null);
  const [payload, setPayload] = useState(null);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [uploadingAttach, setUploadingAttach] = useState(false);
  /** Phase 9: buyer unwind pending_signature from agreement page */
  const [buyerUnwindSubmitting, setBuyerUnwindSubmitting] = useState(false);

  const uploadCompanySlug = useMemo(() => {
    const t = (payload?.listingTitle || payload?.listing?.title || 'Agreement').trim();
    return t.length ? t.replace(/[^a-zA-Z0-9]+/g, '_').slice(0, 80) : 'Agreement';
  }, [payload?.listingTitle, payload?.listing?.title]);

  const payloadRef = useRef(null);
  useEffect(() => {
    payloadRef.current = payload;
  }, [payload]);

  const persistAgreementDocuments = useCallback(
    async (body) => {
      const res = await patchInfrastructureAgreementDocuments(listingIdNum, enquiryIdNum, body);
      if (res?.success && res.data?.agreement) {
        setPayload((prev) => (prev ? { ...prev, agreement: res.data.agreement } : prev));
        toast.success(res.message || 'Documents saved.');
      } else {
        toast.error(res?.message || 'Could not save documents.');
      }
    },
    [listingIdNum, enquiryIdNum]
  );

  const uploadAttachmentBatch = useCallback(
    async (fileList) => {
      const companyName = uploadCompanySlug;
      const all = [];
      for (let i = 0; i < fileList.length; i += 10) {
        const batch = fileList.slice(i, i + 10);
        const formData = new FormData();
        batch.forEach((file) => {
          formData.append('files', file);
        });
        formData.append('category', 'company');
        formData.append('companyName', companyName);
        formData.append('fileType', 'infrastructure_agreement_attachment');
        const res = await CommonService.post('/upload/multiple', formData);
        const fileResults = res?.data?.files;
        if (!res?.success || !Array.isArray(fileResults) || fileResults.length !== batch.length) {
          throw new Error(res?.message || 'Upload failed');
        }
        batch.forEach((file, j) => {
          const ap = fileResults[j]?.accessPath;
          if (!ap) {
            throw new Error('No file path in upload response');
          }
          all.push({ path: ap, name: file.name });
        });
      }
      return all;
    },
    [uploadCompanySlug]
  );

  const onAgreementPdfSelected = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      if (!file) return;
      const ds = String(payloadRef.current?.enquiry?.dealStatus || '').toLowerCase();
      if (ds !== 'pending_signature' && ds !== 'accepted') {
        toast.warning('Documents can only be updated for an active deal.');
        return;
      }
      if (file.size > 25 * 1024 * 1024) {
        toast.error('PDF must be 25MB or smaller.');
        return;
      }
      setUploadingPdf(true);
      try {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('category', 'company');
        fd.append('companyName', uploadCompanySlug);
        fd.append('fileType', 'infrastructure_agreement_pdf');
        const res = await CommonService.post('/upload/single', fd);
        if (!res?.success || !res.data?.accessPath) {
          throw new Error(res?.message || 'Upload failed');
        }
        await persistAgreementDocuments({ agreementDocumentPath: res.data.accessPath });
      } catch (err) {
        toast.error(err?.response?.data?.message || err?.message || 'Upload failed.');
      } finally {
        setUploadingPdf(false);
      }
    },
    [uploadCompanySlug, persistAgreementDocuments]
  );

  const onAttachmentFilesSelected = useCallback(
    async (e) => {
      const picked = Array.from(e.target.files || []);
      e.target.value = '';
      if (!picked.length) return;
      const ds = String(payloadRef.current?.enquiry?.dealStatus || '').toLowerCase();
      if (ds !== 'pending_signature' && ds !== 'accepted') {
        toast.warning('Documents can only be updated for an active deal.');
        return;
      }
      const maxBytes = 25 * 1024 * 1024;
      for (const f of picked) {
        if (f.size > maxBytes) {
          toast.error('Each file must be 25MB or smaller.');
          return;
        }
      }
      setUploadingAttach(true);
      try {
        const uploaded = await uploadAttachmentBatch(picked);
        const prevAgr = payloadRef.current?.agreement;
        const merged = [...parseAgreementAttachments(prevAgr), ...uploaded];
        await persistAgreementDocuments({ attachments: merged });
      } catch (err) {
        toast.error(err?.response?.data?.message || err?.message || 'Upload failed.');
      } finally {
        setUploadingAttach(false);
      }
    },
    [uploadAttachmentBatch, persistAgreementDocuments]
  );

  const removeAgreementPdf = useCallback(async () => {
    const ds = String(payloadRef.current?.enquiry?.dealStatus || '').toLowerCase();
    if (ds !== 'pending_signature' && ds !== 'accepted') return;
    setUploadingPdf(true);
    try {
      await persistAgreementDocuments({ agreementDocumentPath: null });
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Could not remove file.');
    } finally {
      setUploadingPdf(false);
    }
  }, [persistAgreementDocuments]);

  const removeAttachmentAt = useCallback(
    async (index) => {
      const ds = String(payloadRef.current?.enquiry?.dealStatus || '').toLowerCase();
      if (ds !== 'pending_signature' && ds !== 'accepted') return;
      const list = parseAgreementAttachments(payloadRef.current?.agreement);
      const next = list.filter((_, i) => i !== index);
      setUploadingAttach(true);
      try {
        await persistAgreementDocuments({ attachments: next });
      } catch (err) {
        toast.error(err?.response?.data?.message || err?.message || 'Could not update attachments.');
      } finally {
        setUploadingAttach(false);
      }
    },
    [persistAgreementDocuments]
  );

  const load = useCallback(async () => {
    if (!Number.isFinite(listingIdNum) || !Number.isFinite(enquiryIdNum)) {
      setLoading(false);
      setErrorKey('invalid');
      return;
    }
    setLoading(true);
    setErrorKey(null);
    try {
      const res = await (isBuyerView
        ? fetchBuyerInfrastructureAgreementPage(listingIdNum, enquiryIdNum)
        : fetchInfrastructureAgreementPage(listingIdNum, enquiryIdNum));
      if (res?.success && res.data) {
        setPayload(res.data);
      } else {
        setPayload(null);
        setErrorKey('error');
      }
    } catch (e) {
      const status = e?.response?.status;
      if (status === 403) setErrorKey('forbidden');
      else if (status === 404) setErrorKey('not_found');
      else {
        setErrorKey('error');
        toast.error(e?.response?.data?.message || e?.message || 'Could not load agreement.');
      }
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [listingIdNum, enquiryIdNum, isBuyerView]);

  useEffect(() => {
    load();
  }, [load]);

  const enquiry = payload?.enquiry;
  const listing = payload?.listing;
  const agr = payload?.agreement;
  const seller = payload?.seller;
  const buyer = payload?.buyer;
  const asset = payload?.asset;
  const enquirySection = payload?.enquirySection;

  const sellerEnquiriesPath =
    Number.isFinite(listingIdNum) && listingIdNum >= 1
      ? `/infrastructure-leasing/my-properties/${listingIdNum}/enquiries`
      : '/infrastructure-leasing/my-properties';

  const handlePrint = () => {
    window.print();
  };

  if (!Number.isFinite(listingIdNum) || !Number.isFinite(enquiryIdNum)) {
    return (
      <div className="container-fluid ile-seller-listing-enquiries py-4">
        <p>Invalid link.</p>
        <Link to={isBuyerView ? '/infrastructure-leasing/my-enquiries' : '/infrastructure-leasing/my-properties'}>
          {isBuyerView ? 'Back to My enquiries' : 'Back to My properties'}
        </Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container-fluid ile-seller-listing-enquiries py-4">
        <div className="text-muted py-5">Loading agreement…</div>
      </div>
    );
  }

  if (errorKey === 'forbidden' || errorKey === 'not_found' || errorKey === 'invalid') {
    return (
      <div className="container-fluid ile-seller-listing-enquiries py-4">
        <div className="border rounded p-4 bg-white mb-3">
          <p className="text-muted mb-3">
            {errorKey === 'forbidden'
              ? 'You do not have access to this agreement.'
              : 'Agreement not found.'}
          </p>
          <Link
            to={isBuyerView ? '/infrastructure-leasing/my-enquiries' : '/infrastructure-leasing/my-properties'}
            className="btn btn-dark"
          >
            {isBuyerView ? 'My enquiries' : 'My properties / assets'}
          </Link>
        </div>
      </div>
    );
  }

  if (!payload || !enquiry || !listing) {
    return (
      <div className="container-fluid ile-seller-listing-enquiries py-4">
        <p className="text-muted">Could not load agreement.</p>
        <Link
          to={isBuyerView ? '/infrastructure-leasing/my-enquiries' : sellerEnquiriesPath}
          className="btn btn-outline-dark"
        >
          {isBuyerView ? 'Back to My enquiries' : 'Back to enquiries'}
        </Link>
      </div>
    );
  }

  const ds = String(enquiry.dealStatus || '').toLowerCase();
  const showSign = !isBuyerView && ds === 'pending_signature';
  const naturalLeaseCompleted = Boolean(agr?.naturalLeaseCompleted ?? enquiry?.naturalLeaseCompleted);
  const statusKey = naturalLeaseCompleted
    ? 'completed'
    : agr?.agreementStatus || (ds === 'accepted' ? 'active' : ds);
  const docUrl =
    agr?.agreementDocumentPath != null && String(agr.agreementDocumentPath).trim()
      ? resolvePublicFileUrl(agr.agreementDocumentPath)
      : null;
  const docCanEdit = !isBuyerView && (ds === 'pending_signature' || ds === 'accepted');
  const attachmentRows = parseAgreementAttachments(agr);
  const showBuyerSignPanel =
    isBuyerView && ds === 'accepted' && agr?.sellerSignedAt && !agr?.buyerSignedAt;

  return (
    <div
      className="container-fluid ile-seller-listing-enquiries ile-agreement-page py-4"
      id="ile-agreement-print-root"
    >
      <nav aria-label="breadcrumb" className="mb-3 d-print-none">
        <ol className="breadcrumb mb-0">
          {isBuyerView ? (
            <>
              <li className="breadcrumb-item">
                <Link to="/dashboard/buyer">Buyer dashboard</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/infrastructure-leasing/my-enquiries">My enquiries</Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                Agreement
              </li>
            </>
          ) : (
            <>
              <li className="breadcrumb-item">
                <Link to="/dashboard/seller">Seller dashboard</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/infrastructure-leasing/my-properties">My properties / assets</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to={`/infrastructure-leasing/my-properties/${listingIdNum}/enquiries`}>Enquiries</Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                Agreement
              </li>
            </>
          )}
        </ol>
      </nav>

      {isBuyerView && Number.isFinite(listingIdNum) && Number.isFinite(enquiryIdNum) ? (
        <div className="ile-buyer-post-sale mb-3 d-print-none">
          <div className="ile-buyer-post-sale__shell">
            <InfrastructureLeasingBuyerPostSaleTabs
              basePath={`/infrastructure-leasing/my-enquiries/${listingIdNum}/${enquiryIdNum}`}
            />
          </div>
        </div>
      ) : null}

      <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-4">
        <div>
          <h1 className="h3 mb-1">Lease agreement</h1>
          <p className="text-muted mb-0 small">
            {payload.listingTitle || listing.title || `Listing #${listingIdNum}`}
            {agr?.agreementReference ? ` · ${agr.agreementReference}` : ''}
          </p>
        </div>
        <div className="d-flex flex-wrap gap-2 d-print-none">
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handlePrint}>
            Download / Print PDF
          </button>
          <Link
            to={isBuyerView ? '/infrastructure-leasing/my-enquiries' : sellerEnquiriesPath}
            className="btn btn-outline-dark btn-sm"
          >
            {isBuyerView ? 'Back to My enquiries' : 'Back to enquiries'}
          </Link>
        </div>
      </div>

      {/* Full-width property card, then basic agreement info — no side-by-side columns at the top */}
      <div className="row g-4 mb-4">
        <div className="col-12">
          <div className="card border shadow-sm overflow-hidden ile-agreement-page__listing-card">
            <div className="ratio ratio-16x9 bg-light border-bottom ile-agreement-page__listing-media">
              <img
                src={
                  listing.imagePath
                    ? resolvePublicFileUrl(listing.imagePath)
                    : 'https://via.placeholder.com/1200x400?text=Listing'
                }
                alt=""
                style={{ objectFit: 'cover' }}
                onError={(e) => {
                  e.currentTarget.src = 'https://via.placeholder.com/1200x400?text=Listing';
                }}
              />
            </div>
            <div className="card-body py-3 px-4">
              <h2 className="h5 mb-2">{listing.title}</h2>
              <p className="small text-muted mb-2">{listing.locationLine}</p>
              <p className="mb-0 fw-semibold">{listing.pricePrimary || listing.price}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-4 mb-4">
        <div className="col-12">
          <SectionCard title="1. Basic agreement info">
            <dl className="row mb-0 ile-agreement-page__dl">
              <DlRow label="Agreement ID" value={agr?.agreementReference || '—'} />
              <DlRow label="Agreement date" value={formatDateOnly(agr?.agreementDate)} />
              <dt className="col-sm-4 text-muted">Status</dt>
              <dd className="col-sm-8 mb-2">
                <span className={`badge ${statusBadgeClass(statusKey)}`}>
                  {agr?.displayAgreementStatusLabel || agr?.agreementStatusLabel || enquiry.displayStatusLabel || enquiry.statusLabel}
                </span>
              </dd>
            </dl>
          </SectionCard>
        </div>
      </div>

      <div className="row g-4">
        <div className="col-12 ile-agreement-page__rest">
          <div className="row g-3 mb-3 ile-agreement-page__pair">
            <div className="col-12 col-xl-6 d-flex">
              <SectionCard title="2. Seller details" stretch className="flex-grow-1 w-100">
                <dl className="row mb-0 ile-agreement-page__dl">
                  <DlRow label="Seller name" value={seller?.displayName} />
                  <DlRow label="Company name" value={seller?.companyName} />
                  <DlRow label="Email" value={seller?.email} />
                  <DlRow label="Phone" value={seller?.phone} />
                  <DlRow label="Address" value={seller?.addressLine} />
                </dl>
              </SectionCard>
            </div>
            <div className="col-12 col-xl-6 d-flex">
              <SectionCard title="3. Buyer details" stretch className="flex-grow-1 w-100">
                <dl className="row mb-0 ile-agreement-page__dl">
                  <DlRow label="Buyer name" value={buyer?.displayName} />
                  <DlRow label="Company name" value={buyer?.companyName} />
                  <DlRow label="Email" value={buyer?.email} />
                  <DlRow label="Phone" value={buyer?.phone} />
                  <DlRow label="Address" value={buyer?.addressLine} />
                </dl>
              </SectionCard>
            </div>
          </div>

          <div className="row g-3 mb-3 ile-agreement-page__pair">
            <div className="col-12 col-xl-6 d-flex">
              <SectionCard title="4. Property / asset details" stretch className="flex-grow-1 w-100">
                <dl className="row mb-0 ile-agreement-page__dl">
                  <DlRow label="Asset / property name" value={asset?.assetName} />
                  <DlRow label="Asset type" value={asset?.assetTypeLabel} />
                  <DlRow label="Category" value={asset?.listingCategoryLabel} />
                  <DlRow label="Location" value={asset?.locationLine} />
                  <dt className="col-sm-4 text-muted">Description</dt>
                  <dd className="col-sm-8 mb-2 text-break">{asset?.description}</dd>
                  <dt className="col-sm-4 text-muted">Key specifications</dt>
                  <dd className="col-sm-8 mb-0 text-break">{asset?.keySpecifications}</dd>
                </dl>
              </SectionCard>
            </div>
            <div className="col-12 col-xl-6 d-flex">
              <SectionCard title="5. Enquiry details" stretch className="flex-grow-1 w-100">
                <dl className="row mb-0 ile-agreement-page__dl">
                  <DlRow label="Enquiry ID" value={enquirySection?.enquiryId != null ? `#${enquirySection.enquiryId}` : '—'} />
                  <DlRow label="Enquiry date" value={formatDateIso(enquirySection?.enquiryDate)} />
                  <dt className="col-sm-4 text-muted">Buyer message / requirements</dt>
                  <dd className="col-sm-8 mb-2 text-break">{enquirySection?.buyerMessage || '—'}</dd>
                  <dt className="col-sm-4 text-muted">Negotiated terms</dt>
                  <dd className="col-sm-8 mb-0 text-break">{enquirySection?.negotiatedTerms || '—'}</dd>
                </dl>
              </SectionCard>
            </div>
          </div>

          <div className="row g-3 mb-3 ile-agreement-page__pair">
            <div className="col-12 col-xl-6 d-flex">
              <SectionCard title="6. Agreement terms" stretch className="flex-grow-1 w-100">
                <dl className="row mb-0 ile-agreement-page__dl">
                  <DlRow label="Lease start" value={formatDateOnly(agr?.leaseStartDate)} />
                  <DlRow label="Lease end" value={formatDateOnly(agr?.leaseEndDate)} />
                  <DlRow
                    label="Lease duration"
                    value={
                      agr?.leaseDurationMonths != null ? `${agr.leaseDurationMonths} month(s)` : '—'
                    }
                  />
                  <DlRow label="Rental / lease amount" value={agr?.rentalAmount} />
                  <DlRow
                    label="Payment frequency"
                    value={agr?.paymentFrequencyLabel || agr?.paymentFrequency || '—'}
                  />
                  <DlRow label="Security deposit" value={agr?.securityDeposit} />
                  <dt className="col-sm-4 text-muted">Payment terms &amp; conditions</dt>
                  <dd className="col-sm-8 mb-2 text-break">{agr?.paymentTerms || '—'}</dd>
                  <dt className="col-sm-4 text-muted">Renewal terms</dt>
                  <dd className="col-sm-8 mb-0 text-break">{agr?.renewalTerms || '—'}</dd>
                </dl>
              </SectionCard>
            </div>
            <div className="col-12 col-xl-6 d-flex">
              <SectionCard title="7. Additional terms &amp; conditions" stretch className="flex-grow-1 w-100">
                <p className="fw-semibold mb-1 small text-muted">Custom terms (seller)</p>
                <p className="mb-3 text-break">{agr?.additionalTerms || '—'}</p>
                <p className="fw-semibold mb-1 small text-muted">Legal clauses (optional)</p>
                <p className="mb-0 text-break">{agr?.legalClauses || '—'}</p>
              </SectionCard>
            </div>
          </div>

          <div className="row g-3 mb-3 ile-agreement-page__pair">
            <div className="col-12 col-xl-6 d-flex">
              <SectionCard title="8. Documents" stretch className="flex-grow-1 w-100">
                <div className="small">
                  <div className="mb-3">
                    <div className="fw-semibold mb-1">Agreement PDF</div>
                    {docUrl ? (
                      <div className="mb-2">
                        <a href={docUrl} target="_blank" rel="noopener noreferrer">
                          View uploaded document
                        </a>
                        {docCanEdit ? (
                          <button
                            type="button"
                            className="btn btn-link btn-sm py-0 px-1 align-baseline d-print-none"
                            disabled={uploadingPdf}
                            onClick={removeAgreementPdf}
                          >
                            Remove
                          </button>
                        ) : null}
                      </div>
                    ) : (
                      <p className="text-muted mb-2">No agreement PDF uploaded yet.</p>
                    )}
                    {docCanEdit ? (
                      <div className="d-print-none">
                        <label className="form-label text-muted mb-1 small">Upload PDF</label>
                        <input
                          type="file"
                          accept="application/pdf,.pdf"
                          className="form-control form-control-sm"
                          disabled={uploadingPdf || uploadingAttach}
                          onChange={onAgreementPdfSelected}
                        />
                        {uploadingPdf ? (
                          <span className="text-muted ms-2">Uploading…</span>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                  <div>
                    <div className="fw-semibold mb-1">Attachments</div>
                    {attachmentRows.length ? (
                      <ul className="list-unstyled mb-2 small">
                        {attachmentRows.map((row, idx) => {
                          const href = attachmentHref(row);
                          const label = row.name || row.path || `File ${idx + 1}`;
                          return (
                            <li key={`${row.path || idx}-${idx}`} className="d-flex flex-wrap align-items-center gap-2 mb-1">
                              {href ? (
                                <a href={href} target="_blank" rel="noopener noreferrer">
                                  {label}
                                </a>
                              ) : (
                                <span>{label}</span>
                              )}
                              {docCanEdit ? (
                                <button
                                  type="button"
                                  className="btn btn-link btn-sm text-danger py-0 px-1 d-print-none"
                                  disabled={uploadingAttach}
                                  onClick={() => removeAttachmentAt(idx)}
                                >
                                  Remove
                                </button>
                              ) : null}
                            </li>
                          );
                        })}
                      </ul>
                    ) : (
                      <p className="text-muted mb-2">No attachments yet.</p>
                    )}
                    {docCanEdit ? (
                      <div className="d-print-none">
                        <label className="form-label text-muted mb-1 small">
                          Add attachments (PDF, Office docs, images — max 25MB each)
                        </label>
                        <input
                          type="file"
                          multiple
                          accept="application/pdf,.pdf,image/*,.doc,.docx,.xls,.xlsx"
                          className="form-control form-control-sm"
                          disabled={uploadingPdf || uploadingAttach}
                          onChange={onAttachmentFilesSelected}
                        />
                        {uploadingAttach ? (
                          <span className="text-muted ms-2">Uploading…</span>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </div>
              </SectionCard>
            </div>
            <div className="col-12 col-xl-6 d-flex">
              <SectionCard title="9. Signatures" stretch className="flex-grow-1 w-100">
                <dl className="row mb-0 ile-agreement-page__dl">
                  <DlRow label="Seller signature" value={agr?.sellerSignedAt ? 'Recorded (electronic)' : 'Pending'} />
                  <DlRow label="Signer name (seller)" value={agr?.sellerSignerName || '—'} />
                  <DlRow label="Signer designation (seller)" value={agr?.sellerSignerDesignation || '—'} />
                  <dt className="col-sm-4 text-muted">Signature image (seller)</dt>
                  <dd className="col-sm-8 mb-2">
                    {agr?.sellerSignaturePath ? (
                      <img
                        src={resolvePublicFileUrl(agr.sellerSignaturePath)}
                        alt="Seller signature"
                        className="img-fluid border rounded"
                        style={{ maxHeight: 96, objectFit: 'contain' }}
                      />
                    ) : (
                      '—'
                    )}
                  </dd>
                  <DlRow label="Date of signing (seller)" value={formatDateIso(agr?.sellerSignedAt)} />
                  <DlRow label="Buyer signature" value={agr?.buyerSignedAt ? 'Recorded (electronic)' : 'Pending'} />
                  <DlRow label="Signer name (buyer)" value={agr?.buyerSignerName || '—'} />
                  <DlRow label="Signer designation (buyer)" value={agr?.buyerSignerDesignation || '—'} />
                  <dt className="col-sm-4 text-muted">Signature image (buyer)</dt>
                  <dd className="col-sm-8 mb-2">
                    {agr?.buyerSignaturePath ? (
                      <img
                        src={resolvePublicFileUrl(agr.buyerSignaturePath)}
                        alt="Buyer signature"
                        className="img-fluid border rounded"
                        style={{ maxHeight: 96, objectFit: 'contain' }}
                      />
                    ) : (
                      '—'
                    )}
                  </dd>
                  <DlRow label="Date of signing (buyer)" value={formatDateIso(agr?.buyerSignedAt)} />
                </dl>
              </SectionCard>
            </div>
          </div>

          {payload?.notificationSummary ? (
            <div className="card border shadow-sm mb-3 d-print-none">
              <div className="card-header bg-white fw-semibold py-2">Notifications</div>
              <div className="card-body small">
                <p className="mb-1">
                  Unread for this lease:{' '}
                  <strong>{payload.notificationSummary.unreadCountForEnquiry ?? 0}</strong>
                </p>
                {payload.notificationSummary.latestNotificationTitle ? (
                  <p className="text-muted mb-0">
                    Latest: {payload.notificationSummary.latestNotificationTitle}
                    {payload.notificationSummary.latestNotificationAt ? (
                      <span className="text-muted"> · {formatDateIso(payload.notificationSummary.latestNotificationAt)}</span>
                    ) : null}
                  </p>
                ) : (
                  <p className="text-muted mb-0">No in-app notifications yet for this enquiry.</p>
                )}
              </div>
            </div>
          ) : null}

          {!isBuyerView && Number.isFinite(listingIdNum) && Number.isFinite(enquiryIdNum) ? (
            <InfrastructureLeasingDisputePanel variant="seller" listingIdNum={listingIdNum} enquiryIdNum={enquiryIdNum} />
          ) : null}

          <div className="card border shadow-sm d-print-none">
            <div className="card-header bg-white fw-semibold py-2">10. Actions</div>
            <div className="card-body small py-3">
              <div className="d-flex flex-wrap gap-2 align-items-center">
                {isBuyerView ? (
                  <>
                    {showBuyerSignPanel ? (
                      <div className="w-100">
                        <InfrastructureLeasingAgreementSignPanel
                          signRole="buyer"
                          listingIdNum={listingIdNum}
                          enquiryIdNum={enquiryIdNum}
                          uploadCompanySlug={uploadCompanySlug}
                          defaultSignerName={buyer?.displayName || buyer?.companyName || ''}
                          onSigned={() => {
                            navigate(
                              `/infrastructure-leasing/my-enquiries/${listingIdNum}/${enquiryIdNum}/payment`,
                              { replace: true }
                            );
                          }}
                        />
                      </div>
                    ) : ds === 'pending_signature' ? (
                      <div className="d-flex flex-column gap-2 align-items-start">
                        <p className="mb-0 small text-muted">
                          The seller will attach the agreement PDF and complete the electronic signature. Review the terms in
                          sections 1–9 while this is in progress.
                        </p>
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm"
                          disabled={buyerUnwindSubmitting}
                          onClick={async () => {
                            if (
                              !window.confirm(
                                'Cancel this pending deal? Reserved capacity (if any) will be released and the agreement will be cancelled.'
                              )
                            ) {
                              return;
                            }
                            setBuyerUnwindSubmitting(true);
                            try {
                              const res = await postBuyerUnwindPendingSignatureDeal(
                                listingIdNum,
                                enquiryIdNum,
                                {}
                              );
                              if (res?.success) {
                                toast.success(res.message || 'Pending deal cancelled.');
                                navigate('/infrastructure-leasing/my-enquiries', { replace: true });
                              } else {
                                toast.error(res?.message || 'Could not cancel pending deal.');
                              }
                            } catch (e) {
                              const msg = e?.response?.data?.message || e?.message;
                              toast.error(msg || 'Could not cancel pending deal.');
                            } finally {
                              setBuyerUnwindSubmitting(false);
                            }
                          }}
                        >
                          {buyerUnwindSubmitting ? 'Cancelling…' : 'Cancel pending deal'}
                        </button>
                      </div>
                    ) : (ds === 'accepted' || ds === 'completed') && agr?.buyerSignedAt ? (
                      <div className="d-flex flex-column gap-2">
                        <p
                          className={
                            naturalLeaseCompleted ? 'mb-0 text-muted small' : 'mb-0 text-success small'
                          }
                        >
                          You have signed this agreement. Deal status:{' '}
                          <strong>{enquiry.displayStatusLabel ?? enquiry.statusLabel}</strong>.
                        </p>
                        {ds !== 'completed' &&
                        String(agr?.buyerPostSaleStage || '').toLowerCase() === 'payment_due' ? (
                          <Link
                            to={`/infrastructure-leasing/my-enquiries/${listingIdNum}/${enquiryIdNum}/payment`}
                            className="btn btn-dark btn-sm align-self-start"
                          >
                            Continue to payment
                          </Link>
                        ) : null}
                      </div>
                    ) : ds === 'accepted' ? (
                      <p className="mb-0 small text-muted">
                        Waiting for the seller to complete signing on the formal agreement. Deal status:{' '}
                        <strong>Accepted</strong>.
                      </p>
                    ) : (
                      <p className="mb-0 text-muted small">
                        Current status: <strong>{enquiry.displayStatusLabel ?? enquiry.statusLabel}</strong>.
                      </p>
                    )}
                  </>
                ) : showSign ? (
                  <div className="w-100">
                    <InfrastructureLeasingAgreementSignPanel
                      listingIdNum={listingIdNum}
                      enquiryIdNum={enquiryIdNum}
                      uploadCompanySlug={uploadCompanySlug}
                      defaultSignerName={seller?.displayName || seller?.companyName || ''}
                      onSigned={() => {
                        void load();
                      }}
                    />
                  </div>
                ) : ds === 'accepted' || ds === 'completed' ? (
                  <p
                    className={naturalLeaseCompleted ? 'mb-0 text-muted small' : 'mb-0 text-success small'}
                  >
                    This agreement has been signed by the seller. Deal status:{' '}
                    <strong>{enquiry.displayStatusLabel ?? enquiry.statusLabel}</strong>.
                  </p>
                ) : (
                  <p className="mb-0 text-muted small">
                    Signing is only available when the deal is awaiting your signature. Current status:{' '}
                    <strong>{enquiry.displayStatusLabel ?? enquiry.statusLabel}</strong>.
                  </p>
                )}
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handlePrint}>
                  Download / Print PDF
                </button>
                <button
                  type="button"
                  className="btn btn-outline-dark btn-sm"
                  onClick={() => navigate(-1)}
                >
                  Cancel / Back
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
